<script>
  import { mapActions } from "vuex";
  import { socket } from "@/socket";
  import { io } from "socket.io-client";
  import UserMenu from "@/common/userMenu.vue";
  import NotifySidebar from "./NotifySidebar.vue";
  import policyLocations from "@/mixins/policyLocations";

  export default {
    components: { UserMenu, NotifySidebar },
    mixins: [policyLocations],
    data() {
      return {
        notify: [],
        status: false,
        isLoading: false,
        pwSocket: null,
      };
    },
    computed: {
      onlineUsers() {
        return this.$store.getters["connectedUsers/getOnlineUsers"];
      },
      filteredOnlineUsers() {
        const payload = this.$policyLocationsPayload;
        if (!payload) return this.onlineUsers;
        return this.onlineUsers.filter((user) => {
          if (user.role === "model") {
            const cityName = user.roomTemp?.cityName;
            if (!cityName) return false;
            const allowedOffices = payload[cityName];
            if (allowedOffices === undefined) return false;
            if (!allowedOffices.length) return true;
            return allowedOffices.includes(user.roomTemp?.officeName);
          }
          const cityName = user.city;
          if (!cityName) return false;
          const allowedOffices = payload[cityName];
          if (allowedOffices === undefined) return false;
          if (!allowedOffices.length) return true;
          return allowedOffices.includes(user.office);
        });
      },
      user() {
        return this.$profile;
      },
      socketIsReady() {
        const socketReady = this.$store.getters["connectedUsers/getSocketState"];
        if (socketReady) {
          this.initSocketListeners();
        }
      },
      showBell() {
        return this.user.role === "superadmin" && this.user.city !== "Global" && this.$screen.width >= 768;
      },
      isSuperAdmins() {
        return this.user.role === "superadmin";
      },
    },
    watch: {
      $route(to, from) {
        const el = document.getElementById("sidebar-right");
        if (!el.style.display) {
          this.status = false;
          this.$store.state.user.activeNotification = false;
        }
      },
    },
    methods: {
      ...mapActions("applications", ["setShowApps"]),
      ...mapActions("platforms", ["getPasswordChangeHistories"]),
      getToOperation({ role }) {
        return role === "superadmin" ? "/occupation" : "/operation";
      },
      permitLog(user) {
        return !["model", "employee"].includes(user.role);
      },
      statusSidebar() {
        const state = !this.status;
        this.$store.state.user.activeNotification = state;
        if (state) {
          this.getNotifications();
          this.resetNotifications();
        }
      },
      async getNotifications() {
        const numberNotifies = 10;
        this.isLoading = true;
        const res = await this.$axios.get(`/notifications/?limit=${numberNotifies}`);
        this.isLoading = false;
        this.notify = res.data;
        this.closeAlerts();
      },
      resetNotifications() {
        localStorage.setItem("acount", 0);
        this.$store.state.user.accountant = 0;
      },
      closeAlerts() {
        this.$root.$emit("closeToast");
      },
      async updateWithModalOpen() {
        const numberNotifies = 10;
        const res = await this.$axios.get(`/notifications/?limit=${numberNotifies}`);
        this.notify = res.data;
        localStorage.setItem("acount", 0);
        this.$store.state.user.accountant = 0;
      },
      initSocketListeners() {
        socket.on("newNotification", async () => this.status && (await this.updateWithModalOpen()));
      },
      async showChangePassword() {
        if (!this.isSuperAdmins) return;
        this.$store.commit("user/resetCompletedPasswordChanges");
        try {
          const data = await this.getPasswordChangeHistories();

          let process = null;
          if (Array.isArray(data) && data.length > 0) {
            process = data[0];
          } else if (data?._id) {
            process = data;
          } else if (Array.isArray(data?.data) && data.data.length > 0) {
            process = data.data[0];
          } else if (data?.data?._id) {
            process = data.data;
          }

          this.$store.state.user.passwordChangeInProcess = !!process;

          if (process) {
            const users = Array.isArray(process.users) ? process.users : [];
            const total = users.length;
            const completed = users.filter((u) => u.status === "completed").length;
            const percentage = process.percentage ?? (total > 0 ? Math.round((completed / total) * 100) : 0);
            this.$store.commit("user/setPasswordChangeProgress", { percentage, completed, total });
          }

          const hasFailedUsers = (process?.users || []).some((u) => u.status === "failed");
          this.$store.commit("user/setPasswordChangeErrorDetected", !!process?.errorDetected || hasFailedUsers);

          if (process?.captchaDetected) {
            this.$store.commit("user/setPasswordChangeCaptchaData", {
              detected: true,
              retriesExecuted: !!process.retriesExecuted,
              users: (process.users || []).filter((u) => u.errorDescription),
            });
          } else {
            this.$store.commit("user/setPasswordChangeCaptchaData", null);
          }
        } catch {
          this.$store.state.user.passwordChangeInProcess = false;
          this.$store.commit("user/setPasswordChangeCaptchaData", null);
          this.$store.commit("user/setPasswordChangeErrorDetected", false);
        }
        this.$store.state.user.toShowChangePassword = 1;
      },
      connectPasswordSocket() {
        if (!this.isSuperAdmins) return;
        const url = process.env.VUE_APP_CHANGE_PASSWORD_SOCKET;
        if (!url) return;
        this.pwSocket = io(url, { transports: ["websocket"] });

        this.pwSocket.on("passwordChange:progress", (data) => {
          if (data.status !== "processed") return;
          this.$store.commit("user/setPasswordChangeProgress", {
            percentage: data.percentage ?? 0,
            completed: data.completed ?? 0,
            total: data.total ?? 0,
          });
        });

        this.pwSocket.on("passwordChange:completed", () => {
          const modalIsOpen = this.$store.state.user.toShowChangePassword === 1;
          if (!modalIsOpen) {
            this.$store.commit("user/incrementCompletedPasswordChanges");
          }
          this.$store.commit("user/setPasswordChangeProgress", { percentage: 100, completed: 0, total: 0 });
        });
      },
    },
    mounted() {
      this.$root.$on("closeSidebar", (closeSidebar) => {
        this.status = closeSidebar;
        this.$store.state.user.activeNotification = false;
      });
      this.connectPasswordSocket();
    },
    beforeDestroy() {
      if (this.pwSocket) {
        this.pwSocket.disconnect();
        this.pwSocket = null;
      }
    },
  };
</script>

<template>
  <div class="header" noprint v-bind="socketIsReady">
    <div class="header__content">
      <iconic name="menu" class="header__notifyIcon" @click.native="setShowApps" />

      <div class="header__notify" v-b-toggle.sidebar-right @click="statusSidebar()" v-if="showBell">
        <iconic name="bell" class="header__notifyIcon--bell" />
        <span class="header__notifySize">
          {{ this.$store.state.user.accountant }}
        </span>
      </div>

      <router-link class="header__notify" :to="getToOperation(user)" v-if="permitLog(user)">
        <iconic name="users" class="header__notifyIcon" />
        <span class="header__notifySize">{{ filteredOnlineUsers.length }}</span>
      </router-link>

      <button class="header__notify header__changePasswordBtn" @click="showChangePassword" v-if="isSuperAdmins" aria-label="Cambiar contraseña" type="button">
        <iconic name="change_credentials" class="header__notifyIcon" aria-hidden="true" />
        <span class="header__notifySize" v-if="$store.state.user.completedPasswordChanges > 0">{{ $store.state.user.completedPasswordChanges }}</span>
      </button>

      <AvatarStatus :user="user" :containerWidth="40" :containerHeight="40" @click.native="$refs.menu.toggleMenu()" />
    </div>
    <NotifySidebar :data="notify" :isLoading="isLoading" />
    <user-menu ref="menu" :user="user" />
  </div>
</template>

<style lang="scss">
  .header {
    display: flex;
    justify-content: right;
    width: 100%;
    &__content {
      @include Flex(row, flex-end);
      width: fit-content;
      height: 100%;
      gap: 20px;
      border-left: 1px solid rgba(213, 213, 213, 1);
      padding-left: 16px;
    }
    &__notify {
      position: relative;
      @include Flex(column);
      padding: 6px 2px 2px 2px;
      cursor: pointer;
    }
    &__notifyIcon {
      font-size: 22px;
      cursor: pointer;
      color: $primary-color;
      &--bell {
        color: $primary-color;
        font-size: 23px;
      }
    }
    &__notifySize {
      position: absolute;
      @include Row();
      right: -5px;
      top: -5px;
      min-width: 15px;
      min-height: 15px;
      padding: 0 2px;
      font-size: 10px;
      color: $white;
      background-color: $gray-emperor;
      border-radius: 100%;
    }
    &__changePasswordBtn {
      background: none;
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: opacity 0.2s ease;
      &:hover {
        opacity: 0.8;
      }
      &:focus {
        outline: 2px solid #000;
        outline-offset: 2px;
        border-radius: 4px;
      }
      &:focus:not(:focus-visible) {
        outline: none;
      }
    }
  }
</style>
